import { startOfDay } from 'date-fns/startOfDay';
import isNil from 'lodash/isNil';

import { RANGE_PICKER } from '@nocowanie/common-ui';

import {
    AMENITIES_LABELS,
    PROPERTY_TYPE_LABELS,
    PROVISION_LABELS,
    RATING_LABELS,
    RESERVATION_FEATURES_LABELS,
} from '@app/consts/search-filters.consts';
import { isValidLocationType } from '@app/enums';
import { SearchCriteria, SearchFilters } from '@app/models';

import { isValidRange } from 'libs/core/src/lib/helpers/date.helpers';

type SearchFiltersArrayType = keyof Pick<
    SearchFilters,
    'propertyType' | 'amenities' | 'provision' | 'reservationFeatures'
>;

/**
 * Parse search parameters from URL query params
 * @param searchParams URL search params object
 * @returns Object with parsed searchCriteria and filters
 */
export function parseMapSearchParams(searchParams: URLSearchParams | Record<string, string>): {
    searchCriteria: Partial<SearchCriteria>;
    filters: Partial<SearchFilters>;
} {
    const params =
        searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);

    const defaultGuests = {
        adultsCount: 2,
        childrenCount: 0,
        childrenAges: [],
        guestCount: 2,
    };

    const result: {
        searchCriteria: Partial<SearchCriteria>;
        filters: Partial<SearchFilters>;
    } = {
        searchCriteria: {
            guests: defaultGuests,
        },
        filters: {
            propertyType: [],
            amenities: [],
            provision: [],
            reservationFeatures: [],
        },
    };

    const validPropertyTypes = new Set(PROPERTY_TYPE_LABELS.map(item => String(item.value)));
    const validAmenities = new Set(AMENITIES_LABELS.map(item => String(item.value)));
    const validProvisions = new Set(PROVISION_LABELS.map(item => String(item.value)));
    const validReservationFeatures = new Set(Object.keys(RESERVATION_FEATURES_LABELS));
    const validRatings = new Set(RATING_LABELS.map(item => String(item.value)));

    const processArrayFilter = (
        filterKey: keyof Pick<
            SearchFilters,
            'propertyType' | 'amenities' | 'provision' | 'reservationFeatures'
        >,
        validSet: Set<string>,
    ) => {
        const paramKey = `${filterKey}[]`;
        const values = params.getAll(paramKey);
        if (values.length > 0) {
            result.filters[filterKey] = values.filter(v => validSet.has(v));
        }
    };

    processArrayFilter('propertyType', validPropertyTypes);
    processArrayFilter('amenities', validAmenities);
    processArrayFilter('provision', validProvisions);
    processArrayFilter('reservationFeatures', validReservationFeatures);

    const childrenAges: number[] = [];

    Array.from(params.entries()).forEach(([key, value]) => {
        if (key.startsWith('childrenAges[')) {
            const age = parseInt(value);
            if (!isNaN(age)) {
                childrenAges.push(age);
            }
            return;
        }
    });

    if (childrenAges.length > 0) {
        if (!result.searchCriteria.guests) {
            result.searchCriteria.guests = defaultGuests;
        }
        result.searchCriteria.guests.childrenAges = childrenAges;
    }

    const location = params.get('location');
    if (location) {
        result.searchCriteria.location = location;
    }

    const checkInDate = params.get('checkInDate');
    const checkOutDate = params.get('checkOutDate');
    if (
        checkInDate &&
        checkOutDate &&
        isValidRange(
            startOfDay(new Date(checkInDate)),
            startOfDay(new Date(checkOutDate)),
            RANGE_PICKER.RULES.MIN_STAY,
            RANGE_PICKER.RULES.MAX_STAY,
        )
    ) {
        result.searchCriteria.checkInDate = checkInDate;
        result.searchCriteria.checkOutDate = checkOutDate;
    }

    const adultsCount = params.get('adultsCount');
    if (adultsCount) {
        const count = parseInt(adultsCount);
        if (!isNaN(count)) {
            if (!result.searchCriteria.guests) {
                result.searchCriteria.guests = defaultGuests;
            }
            result.searchCriteria.guests.adultsCount = count;
        }
    }

    const childrenCount = params.get('childrenCount');
    if (childrenCount) {
        const count = parseInt(childrenCount);
        if (!isNaN(count)) {
            if (!result.searchCriteria.guests) {
                result.searchCriteria.guests = defaultGuests;
            }
            result.searchCriteria.guests.childrenCount = count;
        }
    }

    const guestCount = params.get('guestCount');
    if (guestCount) {
        const count = parseInt(guestCount);
        if (!isNaN(count)) {
            if (!result.searchCriteria.guests) {
                result.searchCriteria.guests = defaultGuests;
            }
            result.searchCriteria.guests.guestCount = count;
        }
    }

    const geoId = params.get('geoId');
    if (geoId) {
        result.searchCriteria.geoId = geoId;
    }

    const type = params.get('type');
    if (type && isValidLocationType(type)) {
        result.searchCriteria.locationType = type;
    }

    const flexDates = params.get('flexDates');
    if (flexDates) {
        const flexDatesValue = parseInt(flexDates);
        if (!isNaN(flexDatesValue)) {
            result.searchCriteria.flexDates = flexDatesValue;
        }
    }

    const debug = params.get('debug');
    if (debug) {
        const debugValue = parseInt(debug);
        if (!isNaN(debugValue)) {
            result.searchCriteria.debug = debugValue;
        }
    }

    const oneAlgorithm = params.get('oneAlgorithm');
    if (oneAlgorithm) {
        const oneAlgorithmValue = parseInt(oneAlgorithm);
        if (!isNaN(oneAlgorithmValue)) {
            result.searchCriteria.oneAlgorithm = oneAlgorithmValue;
        }
    }

    const indexVersion = params.get('indexVersion');
    if (indexVersion) {
        const indexVersionValue = parseInt(indexVersion);
        if (!isNaN(indexVersionValue)) {
            result.searchCriteria.indexVersion = indexVersionValue;
        }
    }

    const rating = params.get('rating');
    if (rating && validRatings.has(rating)) {
        const ratingValue = parseFloat(rating);
        if (!isNaN(ratingValue)) {
            result.filters.rating = ratingValue;
        }
    }

    const priceTo = params.get('priceTo');
    if (priceTo) {
        const priceToValue = parseInt(priceTo);
        if (!isNaN(priceToValue)) {
            result.filters.priceTo = priceToValue;
        }
    }

    const priceFrom = params.get('priceFrom');
    if (priceFrom) {
        const priceFromValue = parseInt(priceFrom);
        if (!isNaN(priceFromValue)) {
            result.filters.priceFrom = priceFromValue;
        }
    }

    // Handle legacy parameters for navigating back
    const sort = params.get('sort');
    if (sort) {
        result.filters.sort = sort;
    }

    const onlineBooking = params.get('onlineBooking');
    if (onlineBooking) {
        result.filters.onlineBooking = onlineBooking;
    }

    return result;
}

/**
 * Convert search state to URL parameters
 * @param searchCriteria Search criteria object
 * @param filters Filters object
 * @returns URLSearchParams object
 */
export function mapSearchStateToUrlParams(
    searchCriteria: Partial<SearchCriteria>,
    filters: Partial<SearchFilters>,
): URLSearchParams {
    const params = new URLSearchParams();
    if (searchCriteria.location) {
        params.set('location', searchCriteria.location);
    }

    if (searchCriteria.checkInDate) {
        // Use the date string directly or convert to YYYY-MM-DD format if it's an ISO string
        const formattedDate = searchCriteria.checkInDate.includes('T')
            ? searchCriteria.checkInDate.split('T')[0]
            : searchCriteria.checkInDate;
        params.set('checkInDate', formattedDate);
    }

    if (searchCriteria.checkOutDate) {
        // Use the date string directly or convert to YYYY-MM-DD format if it's an ISO string
        const formattedDate = searchCriteria.checkOutDate.includes('T')
            ? searchCriteria.checkOutDate.split('T')[0]
            : searchCriteria.checkOutDate;
        params.set('checkOutDate', formattedDate);
    }

    if (searchCriteria.guests) {
        const { adultsCount, childrenCount, childrenAges, guestCount } = searchCriteria.guests;

        if (adultsCount !== undefined) {
            params.set('adultsCount', adultsCount.toString());
        }

        if (childrenCount !== undefined) {
            params.set('childrenCount', childrenCount.toString());
        }

        if (guestCount !== undefined) {
            params.set('guestCount', guestCount.toString());
        }

        if (childrenAges && childrenAges.length > 0) {
            childrenAges.forEach((age, index) => {
                params.set(`childrenAges[${index}]`, age.toString());
            });
        }
    }

    if (searchCriteria.geoId) {
        params.set('geoId', searchCriteria.geoId);
    }

    if (searchCriteria.locationType) {
        params.set('type', searchCriteria.locationType);
    }

    if (!isNil(searchCriteria.flexDates) && searchCriteria.flexDates > 0) {
        params.set('flexDates', searchCriteria.flexDates.toString());
    }

    if (!isNil(searchCriteria.debug) && searchCriteria.debug > 0) {
        params.set('debug', searchCriteria.debug.toString());
    }

    if (!isNil(searchCriteria.oneAlgorithm) && searchCriteria.oneAlgorithm > 0) {
        params.set('oneAlgorithm', searchCriteria.oneAlgorithm.toString());
    }

    if (!isNil(searchCriteria.indexVersion) && searchCriteria.indexVersion > 0) {
        params.set('indexVersion', searchCriteria.indexVersion.toString());
    }

    // Handle legacy parameters for navigating back
    if (filters.sort) {
        params.set('sort', filters.sort);
    }

    if (filters.onlineBooking) {
        params.set('onlineBooking', filters.onlineBooking);
    }

    const appendFilter = (
        filterKey: keyof Pick<SearchFilters, 'rating' | 'priceFrom' | 'priceTo'>,
    ) => {
        const value = filters[filterKey];
        if (value !== undefined && value !== null) {
            params.set(filterKey, value.toString());
        }
    };

    appendFilter('rating');
    appendFilter('priceFrom');
    appendFilter('priceTo');

    appendAllArrayFiltersToQueryParams(filters, params);

    return params;
}

export const appendArrayFilterToQueryParams = (
    filters: Partial<SearchFilters>,
    params: URLSearchParams,
    filterKey: SearchFiltersArrayType,
) => {
    const values = filters[filterKey];
    if (values && Array.isArray(values) && values.length > 0) {
        values.forEach(value => {
            params.append(`${filterKey}[]`, value);
        });
    }
};

export const appendAllArrayFiltersToQueryParams = (
    filters: Partial<SearchFilters>,
    params: URLSearchParams,
) => {
    const props: SearchFiltersArrayType[] = [
        'propertyType',
        'amenities',
        'provision',
        'reservationFeatures',
    ];

    props.forEach(prop => appendArrayFilterToQueryParams(filters, params, prop));
};
